import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Returns an access JWT that passes `admin.auth.getUser(token)` in Edge Functions.
 *
 * `getSession()` alone can return a cached **expired** access_token while the refresh
 * token is still valid — the client wrapper then forwards a dead JWT in
 * `x-user-token` and functions return 401 / Invalid JWT.
 */
export async function getFreshAccessToken(client: SupabaseClient): Promise<string | null> {
  try {
    const { error: userError } = await client.auth.getUser();
    if (userError) {
      const { data, error: refreshError } = await client.auth.refreshSession();
      if (refreshError || !data.session?.access_token) {
        if (refreshError) {
          // Silent refresh failure was masking actual issues — surface to the
          // console so devs see why edge function calls suddenly 401. Caller
          // still gets null so existing fallbacks (modal prompt, etc.) work.
          console.warn("[supabase-access-token] refresh failed", refreshError.message);
        }
        return null;
      }
      return data.session.access_token;
    }
    const {
      data: { session },
    } = await client.auth.getSession();
    if (session?.access_token) return session.access_token;
    const { data, error: refreshError } = await client.auth.refreshSession();
    if (refreshError || !data.session?.access_token) {
      if (refreshError) {
        console.warn("[supabase-access-token] refresh failed", refreshError.message);
      }
      return null;
    }
    return data.session.access_token;
  } catch (err) {
    console.warn("[supabase-access-token] unexpected error", err);
    return null;
  }
}
